import React, {forwardRef, useMemo} from 'react'
import PropTypes from 'prop-types'
import {Box, Popover, Portal, mergeProps, mergeRefs, useSlotRecipe} from '@chakra-ui/react3'
import {FormattedMessage, useIntl} from 'react-intl'
import {MenuLogoutIcon} from '../icons'
import {NavigationMenuItem} from './internal'
import MyStokkeNavigationDropdown from '../../../pages/my-stokke/partials/navigation-dropdown'
import {useNavigationState} from '../navigation/navigation-provider'
import {menuSlotRecipe} from '../../theme/slot-recipes/menu'
import {Chakra3Theme} from '../../theme'

const A11yTrigger = forwardRef(
    (
        {
            // plain nav list - aria-expanded alone conveys the state
            'aria-haspopup': ariaHasPopup,
            children,
            ...props
        },
        ref
    ) =>
        // merge the way `asChild` does - this sits between it and the real
        // trigger, so a plain clone would drop the trigger's own props
        React.cloneElement(children, {
            ...mergeProps(props, children.props),
            ref: ref ? mergeRefs(ref, children.ref) : children.ref
        })
)

if (process.env.NODE_ENV !== 'production') {
    A11yTrigger.displayName = 'A11yTrigger'
}

A11yTrigger.propTypes = {
    'aria-haspopup': PropTypes.string,
    children: PropTypes.element.isRequired
}

const A11yPanel = forwardRef(
    (
        {
            // site navigation, not a dialog - drop the role and the tab stop so
            // navigation follows the normal Tab order. drop the labelling with
            // them: it points at a title and description this never renders
            role,
            tabIndex,
            'aria-labelledby': ariaLabelledBy,
            'aria-describedby': ariaDescribedBy,
            children,
            ...props
        },
        ref
    ) => {
        const {formatMessage} = useIntl()

        return (
            // portalled out of the header, so it carries its own landmark
            <Box
                as="nav"
                ref={ref}
                aria-label={formatMessage({
                    id: 'header.button.assistive_msg.my_account',
                    defaultMessage: 'My Stokke'
                })}
                {...props}
            >
                {children}
            </Box>
        )
    }
)

if (process.env.NODE_ENV !== 'production') {
    A11yPanel.displayName = 'A11yPanel'
}

A11yPanel.propTypes = {
    role: PropTypes.string,
    tabIndex: PropTypes.number,
    'aria-labelledby': PropTypes.string,
    'aria-describedby': PropTypes.string,
    children: PropTypes.node
}

/**
 * My Stokke account navigation menu used by the desktop header account trigger.
 *
 * Renders the My Stokke navigation links and a sign-out action inside a Chakra
 * popover, styled from the `menu` slot recipe. The panel is a named `nav`: it is
 * portalled to the body, so it leaves the header's landmark and needs its own.
 *
 * Popover rather than menu: it proxies a Tab off the last link to the control
 * that follows the trigger, and leaves focus there instead of pulling it back
 * to the trigger the way the menu machine does on every close (STOK-17609).
 * Selecting an item still returns focus to the trigger.
 *
 * @component
 * @param {Object} props
 * @param {Object|Array} [props.css] - Extra Chakra `css` merged over the panel styles.
 * @param {boolean} [props.defaultOpen] - Opens the menu on initial render.
 * @param {Function} [props.onOpen] - Called when the menu opens.
 * @param {Function} [props.onOpenChange] - Called with Chakra popover open-state details.
 * @param {Function} [props.onSignOutClick] - Called when the sign-out item is selected.
 * @param {React.ReactNode} props.trigger - Account trigger rendered through `Popover.Trigger`.
 * @returns {JSX.Element}
 */
const NavigationMenu = ({
    css,
    defaultOpen,
    onOpen,
    onOpenChange,
    onSignOutClick,
    trigger,
    ...props
}) => {
    const {actions} = useNavigationState()
    const recipe = useSlotRecipe({recipe: menuSlotRecipe})
    const styles = useMemo(() => recipe(), [recipe])

    return (
        <Popover.Root
            unstyled
            lazyMount
            unmountOnExit
            defaultOpen={defaultOpen}
            // keep focus on the trigger, so the key that opened the panel also
            // closes it and the first Tab enters the links
            // eslint-disable-next-line jsx-a11y/no-autofocus -- Chakra's own autoFocus prop, not the DOM autofocus attribute
            autoFocus={false}
            positioning={{
                placement: 'bottom-end'
            }}
            onOpenChange={(details) => {
                actions.close()

                if (details.open) {
                    onOpen?.()
                }

                onOpenChange?.(details)
            }}
        >
            <Popover.Trigger asChild>
                {React.isValidElement(trigger) ? (
                    <A11yTrigger>
                        {React.cloneElement(trigger, {
                            display: {base: 'none', s: 'block'}
                        })}
                    </A11yTrigger>
                ) : (
                    trigger
                )}
            </Popover.Trigger>
            <Portal>
                {/* part of the header, so light like the header */}
                <Chakra3Theme theme="light">
                    <Popover.Positioner display={{base: 'none', s: 'block'}}>
                        <Popover.Content
                            asChild
                            data-testid="header-account-menu"
                            {...props}
                            css={[styles.content, css]}
                        >
                            <A11yPanel>
                                <MyStokkeNavigationDropdown
                                    menuItemComponent={NavigationMenuItem}
                                />
                                <Box as="hr" css={styles.separator} />
                                <NavigationMenuItem
                                    onClick={onSignOutClick}
                                    icon={<MenuLogoutIcon boxSize={4} mt={0.75} />}
                                >
                                    <span>
                                        <FormattedMessage
                                            id="global.button.log_out"
                                            defaultMessage="Log Out"
                                        />
                                    </span>
                                </NavigationMenuItem>
                            </A11yPanel>
                        </Popover.Content>
                    </Popover.Positioner>
                </Chakra3Theme>
            </Portal>
        </Popover.Root>
    )
}

NavigationMenu.propTypes = {
    css: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
    defaultOpen: PropTypes.bool,
    onOpen: PropTypes.func,
    onOpenChange: PropTypes.func,
    onSignOutClick: PropTypes.func,
    trigger: PropTypes.node
}

export {NavigationMenu, NavigationMenuItem}

export default NavigationMenu
